/**
 * Catálogos de filtro dinâmico (uma entrada por coluna) das abas Rúbricas e Cálculo 20S,
 * espelhando RUBRICAS_COLS / CALC20S_COLS do protótipo.
 */
import {
  testePeriodo,
  testeMultiSelect,
  testeRange,
  testeSelect,
  testeTexto,
} from "../../../../components/FiltroCatalogoDinamico/filtroCatalogoDinamico.util";
import { partesIncidenciaRubrica } from "./partesIncidenciaRubrica.util";
import {
  MODOS_VIGENCIA,
  avaliarPeriodoFiltro,
  normalizarVigencias,
  testeVigencia,
} from "./vigenciaRubrica.util";

const AVISO_FORMATO =
  "Informe os meses no formato MM/AAAA (ex.: 06/2022). O filtro não será aplicado.";
const AVISO_INVERTIDO = "Período inválido: o início é maior que o fim. O filtro não será aplicado.";

/** Mensagem do filtro de vigência quando o período digitado não pode ser aplicado. */
function avisoVigencia(ativo) {
  const { status } = avaliarPeriodoFiltro(ativo?.value, ativo?.value2);
  if (status === "formato") return AVISO_FORMATO;
  if (status === "invertido") return AVISO_INVERTIDO;
  return null;
}

export const SEM_BASE_LEGAL_FILTRO = "sem";

const OPCOES_SIM_NAO = [
  { value: "true", label: "Sim" },
  { value: "false", label: "Não" },
];

function valoresDistintos(rows, getter) {
  const set = new Set();
  (rows || []).forEach((r) => {
    const v = getter(r);
    if (v) set.add(String(v));
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
}

/**
 * Dropdown do filtro: com 3+ opções é de múltipla escolha (checkbox + "Todos"); com só 2 é
 * escolha única, pois é "um ou outro" (ex.: Sim/Não). `getter` devolve o valor da linha.
 */
function filtroLista(options, getter) {
  if (options.length <= 2) return { type: "select", options, test: testeSelect(getter) };
  return { type: "multiselect", options, test: testeMultiSelect(getter, options.length) };
}

function opcoesDistintas(rows, getter) {
  return valoresDistintos(rows, getter).map((v) => ({ value: v, label: v }));
}

function textoOuNulo(v) {
  if (v == null) return null;
  const t = String(v).trim();
  return t === "" ? null : t;
}

/**
 * Pré-computa as colunas de incidência (INSS/FGTS/IRRF) uma única vez, para reuso tanto na
 * tabela quanto no catálogo de filtro (TXM-671).
 *
 * INSS: código/descrição vêm da API (domínio); se faltarem, cai no texto legado
 * `incidenciaRubrica` ("descrição - categoria"), que também é a única fonte da categoria.
 * FGTS/IRRF vêm só da API.
 */
export function enriquecerRubricas(rubricas) {
  return (rubricas || []).map((r) => {
    const incid = partesIncidenciaRubrica(r.incidenciaRubrica);
    const inssCodigo = textoOuNulo(r.codigoIncidenciaInss) ?? incid.codigo;
    const inssDescricao = textoOuNulo(r.descricaoIncidenciaInss) ?? incid.descricao;
    return {
      ...r,
      vigencias: normalizarVigencias(r.vigencias),
      inssCodigo,
      inssDescricao,
      inssCategoria: incid.categoria,
      fgtsCodigo: textoOuNulo(r.codigoIncidenciaFgts),
      fgtsDescricao: textoOuNulo(r.descricaoIncidenciaFgts),
      irrfCodigo: textoOuNulo(r.codigoIncidenciaIrrf),
      irrfDescricao: textoOuNulo(r.descricaoIncidenciaIrrf),
    };
  });
}

const DOMINIOS_VAZIOS = { inss: [], fgts: [], irrf: [] };

function opcoesDeDescricao(lista) {
  return (lista || []).map((d) => ({ value: d.descricao, label: d.descricao }));
}

/**
 * @param {Array<object>} rubricasEnriquecidas saída de enriquecerRubricas
 * @param {Array<object>} basesLegais
 * @param {{inss: Array, fgts: Array, irrf: Array}} [dominios] listas oficiais de incidência
 *   (`{codigo, descricao}`) que alimentam os droplists de "Descrição da Incidência".
 */
export function buildCatalogoRubricas(rubricasEnriquecidas, basesLegais, dominios) {
  const dom = { ...DOMINIOS_VAZIOS, ...(dominios || {}) };
  const opcoesBaseLegal = [
    { value: SEM_BASE_LEGAL_FILTRO, label: "Sem base legal (fora do projeto)" },
    ...(basesLegais || []).map((b) => ({ value: String(b.id), label: b.descricao })),
  ];
  return [
    {
      id: "codigoRubrica",
      label: "Código da Rubrica",
      type: "text",
      test: testeTexto((r) => r.codigoRubrica),
    },
    {
      id: "descricaoRubrica",
      label: "Descrição da Rubrica",
      type: "text",
      test: testeTexto((r) => r.descricaoRubrica),
    },
    {
      // TXM-671: substitui "Início da Validade"/"Fim (Calculado) da Validade" (que eram a
      // 1ª/última competência com lançamento) pela vigência real da rúbrica no eSocial.
      id: "periodoVigencia",
      label: "Período de vigência",
      type: "vigencia",
      modos: MODOS_VIGENCIA,
      aviso: avisoVigencia,
      test: testeVigencia,
    },
    {
      id: "tipoRubrica",
      label: "Tipo da Rubrica",
      ...filtroLista(opcoesDistintas(rubricasEnriquecidas, (r) => r.tipoRubrica), (r) => r.tipoRubrica),
    },
    {
      id: "inssCodigo",
      label: "INSS — Cód Incidência",
      type: "text",
      test: testeTexto((r) => r.inssCodigo),
    },
    {
      id: "inssDescricao",
      label: "INSS — Descrição da Incidência",
      rotuloCampo: "Descrição",
      ...filtroLista(opcoesDeDescricao(dom.inss), (r) => r.inssDescricao),
    },
    {
      id: "inssCategoria",
      label: "INSS — Categoria Incidência INSS",
      ...filtroLista(opcoesDistintas(rubricasEnriquecidas, (r) => r.inssCategoria), (r) => r.inssCategoria),
    },
    {
      id: "fgtsCodigo",
      label: "FGTS — Cód Incidência",
      type: "text",
      test: testeTexto((r) => r.fgtsCodigo),
    },
    {
      id: "fgtsDescricao",
      label: "FGTS — Descrição da Incidência",
      rotuloCampo: "Descrição",
      ...filtroLista(opcoesDeDescricao(dom.fgts), (r) => r.fgtsDescricao),
    },
    {
      id: "irrfCodigo",
      label: "IRRF — Cód Incidência",
      type: "text",
      test: testeTexto((r) => r.irrfCodigo),
    },
    {
      id: "irrfDescricao",
      label: "IRRF — Descrição da Incidência",
      rotuloCampo: "Descrição",
      ...filtroLista(opcoesDeDescricao(dom.irrf), (r) => r.irrfDescricao),
    },
    {
      id: "flagIncidencia",
      label: "Incidência da Rubrica",
      ...filtroLista(OPCOES_SIM_NAO, (r) => Boolean(r.flagIncidencia)),
    },
    {
      id: "baseLegal",
      label: "Base Legal",
      ...filtroLista(opcoesBaseLegal, (r) =>
        r.baseLegalId == null ? SEM_BASE_LEGAL_FILTRO : String(r.baseLegalId)
      ),
    },
    {
      id: "verbaFerias",
      label: "Verbas de Afastamento",
      ...filtroLista(OPCOES_SIM_NAO, (r) => Boolean(r.verbaFerias)),
    },
  ];
}

export function buildCatalogo20s() {
  return [
    {
      id: "competencia",
      label: "Competência",
      type: "period",
      test: testePeriodo((r) => r.competencia),
    },
    {
      id: "valorBaseCalculo",
      label: "Base de Cálculo",
      type: "range",
      test: testeRange((r) => r.valorBaseCalculo),
    },
    {
      id: "valorDiferencaBaseCalculada",
      label: "Diferença Base",
      type: "range",
      test: testeRange((r) => r.valorDiferencaBaseCalculada),
    },
    {
      id: "valorCalculo20s",
      label: "Cálculo 20S",
      type: "range",
      test: testeRange((r) => r.valorCalculo20s),
    },
    {
      id: "aliqSelic",
      label: "Taxa Selic",
      type: "range",
      test: testeRange((r) => r.aliqSelic),
    },
    {
      id: "valorAcrescimoSelic",
      label: "Acréscimo Selic",
      type: "range",
      test: testeRange((r) => r.valorAcrescimoSelic),
    },
    {
      id: "valorAtualizado",
      label: "Valor Atualizado",
      type: "range",
      test: testeRange((r) => r.valorAtualizado),
    },
  ];
}
